import { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import IntroScreen from "./components/IntroScreen";
import Assessment from "./components/Assessment";
import LoadingScreen from "./components/LoadingScreen";
import EmailCapture from "./components/EmailCapture";
import Results from "./components/Results";
import AdminPanel from "./components/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// App states: intro | assessment | loading | email | results
function IdentityAnalyzer() {
  const [screen, setScreen] = useState("intro");
  const [assessmentData, setAssessmentData] = useState(null);
  const [error, setError] = useState(null);

  const handleStart = useCallback(() => {
    setScreen("assessment");
  }, []);

  const handleAssessmentComplete = useCallback(async (answers) => {
    setScreen("loading");
    setError(null);

    try {
      const response = await axios.post(`${API}/assessment/submit`, {
        answers,
      });
      setAssessmentData(response.data);
      setTimeout(() => {
        setScreen("email");
      }, 3000);
    } catch (err) {
      console.error("Assessment submission failed:", err);
      setError("Something went wrong generating your results. Please try again.");
      setScreen("assessment");
    }
  }, []);

  const handleEmailSubmit = useCallback(async (formData) => {
    // Send results to GoHighLevel CRM via webhook
    if (assessmentData?.id) {
      try {
        await axios.post(`${API}/assessment/send-to-crm`, {
          assessment_id: assessmentData.id,
          email: formData?.email || "",
          first_name: formData?.first_name || "",
          last_name: formData?.last_name || "",
        });
      } catch (err) {
        console.error("CRM webhook failed:", err);
      }
    }
    setScreen("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [assessmentData]);

  return (
    <div data-testid="app-root">
      {error && (
        <div
          data-testid="error-banner"
          className="fixed top-2 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 px-4 sm:px-6 py-3 font-body text-xs sm:text-sm max-w-sm sm:max-w-md"
        >
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-3 text-red-500 hover:text-red-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {screen === "intro" && <IntroScreen onStart={handleStart} />}
      {screen === "assessment" && (
        <Assessment onComplete={handleAssessmentComplete} />
      )}
      {screen === "loading" && <LoadingScreen />}
      {screen === "email" && (
        <EmailCapture
          onSubmit={handleEmailSubmit}
          primaryArchetype={assessmentData?.primary_archetype}
        />
      )}
      {screen === "results" && <Results data={assessmentData} />}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<IdentityAnalyzer />} />
          <Route path="/admin" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
